/**
 * Ops side shell · OPS-UX (SURF-OPS-L2).
 *
 * Visible L2 ≤5: Now · Dispatch · Areas · Signals · More
 * Map stays the canvas (/ops). AI is INLINE face (/ops/ai), not a peer L2 city.
 * Run / Plant / Cams / Resources live under More (deep links kept).
 * Soft theatre: host-local only; production paints Live buses only.
 * Honesty: label: "Plant"
 */
import { useEffect, _useMemo, useState } from "react";
import { html, go, Ico, M, SOFT, _SPRING } from "./ui.js";
import { isLocalHost } from "./pathseal.js";

/** Primary L2 seats (≤5). Map is canvas, not a seat chip. */
export const OPS_L2_PRIMARY = [
  {
    id: "overview",
    label: "Now",
    title: "Now",
    path: "/ops",
    purpose: "What needs attention on this site",
    ico: "gauge",
    key: "1",
  },
  {
    id: "dispatch",
    label: "Dispatch",
    title: "Dispatch",
    path: "/ops/dispatch",
    purpose: "People and open runs",
    ico: "route",
    key: "2",
  },
  {
    id: "zones",
    label: "Areas",
    title: "Areas",
    path: "/ops/areas",
    purpose: "Focus and boundary",
    ico: "layers",
    key: "3",
  },
  {
    id: "alarms",
    label: "Signals",
    title: "Signals",
    path: "/ops/alarms",
    purpose: "Alert history",
    ico: "siren",
    key: "4",
  },
];

/** More drawer — ops-only desks. Cameras / Resources already own the topbar. */
export const OPS_L2_MORE = [
  {
    id: "workbench",
    label: "Run",
    title: "Run",
    path: "/ops/workbench",
    purpose: "Open jobs and pulse",
    ico: "play",
    key: "5",
  },
  {
    id: "devices",
    label: "Boards",
    title: "Boards",
    path: "/ops/devices",
    purpose: "Boards on this site",
    ico: "chip",
    key: "6",
  },
  {
    id: "resources",
    label: "Ready",
    title: "Readiness",
    path: "/ops/resources",
    purpose: "Coverage and on-call posture",
    ico: "layers",
    key: "7",
  },
];

/** Flat catalogue for helpers (primary + more). Backward-compatible shape. */
export const OPS_PANEL_NAV = [
  {
    id: "jobs",
    label: "On site",
    items: [...OPS_L2_PRIMARY],
  },
  {
    id: "more",
    label: "More",
    items: [...OPS_L2_MORE],
  },
];

export const OPS_PANEL_ROUTES = {
  overview: "/ops",
  workbench: "/ops/workbench",
  zones: "/ops/zones",
  dispatch: "/ops/dispatch",
  devices: "/ops/devices",
  cameras: "/cameras/live",
  alarms: "/ops/alarms",
  resources: "/ops/resources",
};

const MORE_IDS = new Set(OPS_L2_MORE.map((x) => x.id));

export function opsPanelFaceOf(tab) {
  const id = String(tab || "overview");
  if (id === "ai") return "ai";
  return "observe";
}

export function opsPanelSeatsOf(nav = OPS_PANEL_NAV) {
  const out = [];
  for (const g of nav) {
    for (const it of g.items || []) out.push(it);
  }
  return out;
}

export function opsPanelTabLabelsOf(nav = OPS_PANEL_NAV) {
  const out = {
    overview: "Now",
    ai: "AI",
    workbench: "Run",
    zones: "Areas",
    dispatch: "Dispatch",
    devices: "Boards",
    cameras: "Cameras",
    alarms: "Signals",
    resources: "Ready",
  };
  for (const g of nav) {
    for (const it of g.items || []) out[it.id] = it.label;
  }
  return out;
}

export function opsPanelItemOf(tab, nav = OPS_PANEL_NAV) {
  const id = String(tab || "overview");
  if (id === "ai") {
    return {
      id: "ai",
      label: "AI",
      title: "AI Workspace",
      path: "/ops/ai",
      purpose: "AI full control · Hold to interrupt",
      ico: "gauge",
      key: "0",
    };
  }
  if (id === "map") {
    return OPS_L2_PRIMARY[0];
  }
  for (const g of nav) {
    const hit = (g.items || []).find((x) => x.id === id);
    if (hit) return hit;
  }
  if (id === "zones") {
    return OPS_L2_PRIMARY.find((x) => x.id === "zones") || null;
  }
  return null;
}

export function opsPanelFillTab(_tab) {
  return true;
}

export function opsIsMoreTab(tab) {
  return MORE_IDS.has(String(tab || ""));
}

/** @deprecated residual */
export function OpsStageCue() {
  return null;
}

/**
 * Observe chrome · OPS-UX L2 ≤5 + More expander.
 */
export function OpsObserveChrome({ tab, alarmN = 0, activeZoneId = "", onHide }) {
  const local = isLocalHost();
  const inMore = opsIsMoreTab(tab);
  const [moreOpen, setMoreOpen] = useState(inMore);
  const active = opsPanelItemOf(tab);

  useEffect(() => {
    if (inMore) setMoreOpen(true);
  }, [inMore]);

  useEffect(() => {
    const onKey = (e) => {
      if (!e || !e.altKey || e.ctrlKey || e.metaKey) return;
      const t = e.target;
      const tag = t && t.tagName ? String(t.tagName).toUpperCase() : "";
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (t && t.isContentEditable)) {
        return;
      }
      if (String(e.key || "") === "1") {
        e.preventDefault();
        go("/ops");
      }
      if (String(e.key || "") === "0") {
        e.preventDefault();
        go("/ops/ai");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const seatBtn = (it, { more = false } = {}) => {
    const softOff = !!(it.localSoft && !local);
    const on = tab === it.id;
    const badge = it.id === "alarms" && alarmN > 0;
    return html`<button
      key=${it.id}
      type="button"
      role="tab"
      className=${more ? "ops-obs__seat ops-obs__seat--more" : "ops-obs__seat"}
      data-on=${on ? "1" : "0"}
      data-soft=${it.localSoft ? "1" : "0"}
      data-dim=${softOff ? "1" : "0"}
      disabled=${softOff}
      aria-selected=${on ? "true" : "false"}
      title=${softOff ? `${it.label} · lab only` : it.purpose || it.label}
      onClick=${() => !softOff && go(it.path)}>
      <span className="ops-obs__seat-l">${it.label}</span>
      ${badge ? html`<em>${alarmN}</em>` : null}
    </button>`;
  };

  return html`<header
    className="ops-obs"
    data-ops-rail="v620"
    data-field="v622"
    data-cmc-shell="ops-ux"
    data-ops-l2="5"
    data-face="observe"
    data-local=${local ? "1" : "0"}
    data-tab=${tab || ""}
    data-alarms=${alarmN > 0 ? "1" : "0"}
    data-zone=${activeZoneId ? "1" : "0"}
    data-more=${moreOpen || inMore ? "1" : "0"}
    aria-label="Operations">

    <div className="ops-obs__row">
      <button type="button" className="ops-obs__back" onClick=${() => go("/ops/ai")}
        title="Open AI on map" aria-label="Open AI on map">
        AI
      </button>
      <div className="ops-obs__title">
        <b>${tab === "overview" ? "Now" : ((active && active.label) || "Operations")}</b>
      </div>
      <span className="ops-obs__env" data-local=${local ? "1" : "0"}>
        ${local ? "Lab" : "Site"}
      </span>
      ${onHide
        ? html`<button type="button" className="icon-btn" title="Hide" aria-label="Hide panel"
            onClick=${onHide}>
            <${Ico} d="arrowR" w=${13}/>
          </button>`
        : null}
    </div>

    <nav className="ops-obs__seats" role="tablist" aria-label="Operations seats">
      ${OPS_L2_PRIMARY.map((it) => seatBtn(it))}
      <button
        type="button"
        role="tab"
        className="ops-obs__seat"
        data-on=${inMore || moreOpen ? "1" : "0"}
        aria-selected=${inMore || moreOpen ? "true" : "false"}
        aria-expanded=${moreOpen || inMore ? "true" : "false"}
        title="More desks"
        onClick=${() => {
          if (inMore) {
            setMoreOpen(true);
            return;
          }
          setMoreOpen((v) => !v);
        }}>
        <span className="ops-obs__seat-l">More</span>
      </button>
    </nav>

    ${(moreOpen || inMore)
      ? html`<nav className="ops-obs__more" role="tablist" aria-label="More desks">
          ${OPS_L2_MORE.map((it) => seatBtn(it, { more: true }))}
        </nav>`
      : null}

    ${activeZoneId
      ? html`<button type="button" className="ops-obs__zone"
          onClick=${() => {
            try {
              window.dispatchEvent(new CustomEvent("croc-zone-clear"));
            } catch {
              /* */
            }
            go("/ops");
          }}>
          <span className="ops-obs__zone-id" title=${activeZoneId}>${activeZoneId}</span>
          <em>Clear</em>
        </button>`
      : null}

  </header>`;
}

export function OpsPanelRail({ tab, alarmN = 0, activeZoneId = "", onHide }) {
  return html`<${OpsObserveChrome}
    tab=${tab}
    alarmN=${alarmN}
    activeZoneId=${activeZoneId}
    onHide=${onHide}
  />`;
}

export function OpsPanelReveal({ tab, alarmN, onOpen }) {
  const face = opsPanelFaceOf(tab);
  const item = opsPanelItemOf(tab);
  return html`<${M.button}
    type="button"
    className="ops-rail-reveal ops-dock-reveal panel-reveal glass"
    aria-label="Show operations panel"
    title="Show operations panel"
    whileHover=${{ x: -3 }}
    whileTap=${{ scale: 0.98 }}
    transition=${SOFT}
    onClick=${onOpen}>
    <${M.span} className="panel-reveal__ico" aria-hidden="true">
      <${Ico} d=${(item && item.ico) || "gauge"} w=${14} />
    <//>
    <span className="panel-reveal__stack">
      <b>${face === "ai" ? "AI" : (item && item.label) || "Operations"}</b>
      ${alarmN > 0
        ? html`<em className="panel-reveal__badge">${alarmN} alerts</em>`
        : html`<em>${face === "ai" ? "On map" : "Panel"}</em>`}
    </span>
  <//>`;
}
